import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import xor from 'lodash/xor'
import { AnimatePresence, motion } from 'motion/react'
import type { ReactNode } from 'react'
import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import { FAIcon } from '../fa-icon'
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui'
import { type PopoverContentProps } from '@radix-ui/react-popover'
import { comboboxItemMatchesQuery } from './match'

export interface ComboboxItem<TValue extends string | number = string> {
  value: TValue
  label: ReactNode
  /** If not set, will query on label */
  filterValue?: string
}

export interface ComboboxCommonProps<TValue extends string | number> {
  className?: string
  itemClassName?: string
  curtainClassName?: string
  id?: string

  disabled?: boolean
  placeholder?: string | ((items: ComboboxItem<TValue>[]) => string)
  searchPlaceholder?: string
  noResultsText?: string
  noSearch?: boolean

  onOpenChange?(open: boolean): void
  onBlur?(): void

  PopoverContentProps?: Partial<PopoverContentProps>
}

interface SingleProps<TValue extends string | number>
  extends ComboboxCommonProps<TValue> {
  mode: 'single'
  value: TValue
  items: ComboboxItem<TValue>[]
  clearable?: boolean
  onChange(value: TValue): void
}

interface MultiProps<TValue extends string | number>
  extends ComboboxCommonProps<TValue> {
  mode: 'multiple'
  value: TValue[]
  items: ComboboxItem<TValue>[]
  clearable?: false
  onChange(value: TValue[]): void
}

type Props<TValue extends string | number> =
  | SingleProps<TValue>
  | MultiProps<TValue>

export function Combobox<TValue extends string | number>({
  className,
  itemClassName,
  curtainClassName,

  id,
  mode,

  value,
  items,

  disabled = false,
  clearable = false,
  placeholder,
  searchPlaceholder,
  noResultsText,
  noSearch = false,

  onOpenChange,
  onBlur,
  onChange,

  PopoverContentProps,
}: Props<TValue>) {
  const translations = useTranslate({
    single: {
      placeholder: 'components.combobox.placeholder',
      searchPlaceholder: 'components.combobox.search-placeholder',
      noResults: 'components.combobox.no-results',
    },
    multiple: {
      placeholder: 'components.multi-combobox.placeholder',
      searchPlaceholder: 'components.multi-combobox.search-placeholder',
      noResults: 'components.multi-combobox.no-results',
    },
  })

  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const wasOpen = useRef(false)

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [popoverSide, setPopoverSide] = useState<'top' | 'bottom'>('bottom')
  const [popoverWidth, setPopoverWidth] = useState<number>(200)
  const [insideDialog, setInsideDialog] = useState(false)

  /**
   * Measure the trigger from a layout effect rather than a ref callback.
   * Radix composes the trigger ref anew on every render, so a ref callback
   * re-runs on each one — setting state from there feeds the commit phase
   * back into itself and can exhaust React's nested update limit. The
   * functional update keeps an unchanged width from scheduling a render at
   * all.
   */
  useLayoutEffect(() => {
    const el = triggerRef.current
    if (!el) return

    setInsideDialog(!!el.closest('[data-slot="dialog-content"]'))

    const measure = () =>
      setPopoverWidth(current =>
        current === el.offsetWidth ? current : el.offsetWidth
      )

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  function filterFn(item: ComboboxItem<TValue>) {
    if (!!item.filterValue)
      return comboboxItemMatchesQuery(item.filterValue, search)
    if (typeof item.label === 'string')
      return comboboxItemMatchesQuery(item.label, search)
    return true
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    onOpenChange?.(isOpen)
  }

  const filteredItems = !search ? items : items.filter(filterFn)

  const selected =
    mode === 'single'
      ? items.find(item => item.value === value) ?? null
      : items.filter(item => value.includes(item.value))

  const fireBlur = useEffectEvent(() => onBlur?.())

  useEffect(() => {
    if (!open && wasOpen.current) fireBlur()
    wasOpen.current = open
  }, [open])

  const fireOpenChange = useEffectEvent(handleOpenChange)

  useEffect(() => {
    if (!open || !isMobile) return

    function handleTouchEnd(event: TouchEvent) {
      const target = event.target as Node | null
      if (!target) return

      const contentEl = popoverRef.current
      const buttonEl = triggerRef.current

      // If the touch is inside the popover content or trigger button, ignore it
      if (contentEl?.contains(target) || buttonEl?.contains(target)) return

      // Touch happened outside: close the popover
      fireOpenChange(false)
    }

    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    function syncPopoverSide() {
      const side = popoverRef.current?.dataset.side
      if (side === 'top' || side === 'bottom') setPopoverSide(side)
    }

    let raf1 = window.requestAnimationFrame(() => {
      syncPopoverSide()
      raf1 = window.requestAnimationFrame(syncPopoverSide)
    })

    const observer = new MutationObserver(syncPopoverSide)
    if (popoverRef.current) {
      observer.observe(popoverRef.current, {
        attributes: true,
        attributeFilter: ['data-side'],
      })
    }

    return () => {
      window.cancelAnimationFrame(raf1)
      observer.disconnect()
    }
  }, [open])

  const shouldShowPlaceholder =
    (mode === 'single' && !selected) ||
    (mode === 'multiple' && Array.isArray(selected) && selected.length === 0)

  const renderedPlaceholder = !placeholder
    ? translations[mode].placeholder
    : typeof placeholder === 'function'
    ? placeholder(items)
    : placeholder

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={insideDialog}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          className={cn(
            'justify-between w-full min-w-[200px] px-6 bg-quaternary border-gray-200 text-base font-normal',
            (value === null || value === '') && 'text-gray-400',
            !open ? 'hover:border-stone-400' : 'hover:border-gray-200',
            open && popoverSide === 'bottom' && 'border-b-0! rounded-b-none!',
            open && popoverSide === 'top' && 'border-t-0! rounded-t-none!',
            mode === 'multiple' && 'py-1',
            className
          )}
          id={id}
          role="combobox"
          variant="outline"
          disabled={disabled}
          aria-expanded={open}
        >
          {shouldShowPlaceholder ? (
            <span className="text-gray-400">{renderedPlaceholder}</span>
          ) : !Array.isArray(selected) ? (
            selected?.label
          ) : mode === 'multiple' ? (
            <div className="flex flex-wrap gap-1 w-full max-h-full overflow-y-auto">
              <AnimatePresence initial={false} mode="popLayout">
                {selected.map(item => (
                  <motion.div
                    key={item.value}
                    className="flex items-center px-2 py-1 rounded-md text-xs text-slate-100 bg-primary cursor-default"
                    initial={{ opacity: 0, scale: 0.9, y: -5 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -5 }}
                    layout
                    onClick={e => e.stopPropagation()}
                  >
                    <span className="text-ellipsis overflow-hidden whitespace-nowrap">
                      {item.label}
                    </span>

                    <FAIcon
                      className="ml-1 hover:text-slate-300 cursor-pointer"
                      icon="times-circle"
                      type="solid"
                      onClick={() => onChange(xor(value, [item.value]))}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : null}

          {mode === 'single' && clearable && !shouldShowPlaceholder && (
            <FAIcon
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
              icon="times"
              onClick={e => {
                e.stopPropagation()
                onChange('' as TValue)
              }}
            />
          )}

          <FAIcon
            className="text-gray-300"
            icon={open ? 'chevron-up' : 'chevron-down'}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        ref={popoverRef}
        data-detect-blur-ignore="true"
        {...PopoverContentProps}
        className={cn(
          'w-full p-0',
          'data-[side=bottom]:border-t-0 data-[side=bottom]:rounded-t-none',
          'data-[side=top]:border-b-0 data-[side=top]:rounded-b-none data-[side=top]:shadow-md-up',
          PopoverContentProps?.className
        )}
        style={{ width: popoverWidth, ...PopoverContentProps?.style }}
      >
        <Command
          className={cn('bg-quaternary', curtainClassName)}
          shouldFilter={false}
        >
          {!noSearch && (
            <CommandInput
              className="overflow-hidden"
              value={search}
              placeholder={
                searchPlaceholder ?? translations[mode].searchPlaceholder
              }
              autoFocus={!isMobile}
              onValueChange={setSearch}
            />
          )}

          <CommandList>
            <CommandEmpty>
              {noResultsText ?? translations[mode].noResults}
            </CommandEmpty>

            <CommandGroup>
              {filteredItems.map(item => {
                const isSelected =
                  mode === 'single'
                    ? value === item.value
                    : value.includes(item.value)

                return (
                  <CommandItem
                    key={item.value}
                    className={cn(
                      'text-base data-[selected=true]:bg-gray-200',
                      itemClassName
                    )}
                    value={String(item.value)}
                    onSelect={currentValue => {
                      const cur =
                        typeof value === 'number'
                          ? Number(currentValue)
                          : currentValue

                      if (mode === 'single') {
                        const val = cur as TValue
                        onChange(
                          !clearable
                            ? val
                            : val === value
                            ? ('' as TValue)
                            : val
                        )
                        setOpen(false)
                        setSearch('')
                      } else {
                        onChange(xor(value, [cur]) as TValue[])
                      }
                    }}
                  >
                    <span className={cn(isSelected && 'font-semibold')}>
                      {item.label}
                    </span>

                    <FAIcon
                      className={cn(
                        'ml-auto',
                        isSelected ? 'opacity-100' : 'opacity-0'
                      )}
                      icon="check"
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
